import { useState, useEffect } from "react";
import { Calendar, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DailyNews = () => {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/daily-news`);
      setNewsList(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
      toast.error("Failed to load news. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const generateNewArticle = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/generate-daily-news`);
      setNewsList([response.data, ...newsList]);
      toast.success("New article generated successfully!");
    } catch (error) {
      console.error('Error generating news:', error);
      toast.error("Failed to generate article. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="news-title">
            Daily Herb News
          </h1>
          <p className="text-gray-600 mb-6" data-testid="news-subtitle">
            Stay informed with the latest research, trends, and insights in herbal medicine
          </p>
          <Button
            onClick={generateNewArticle}
            disabled={generating}
            className="rounded-full"
            data-testid="generate-news-btn"
          >
            {generating ? (
              <>
                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 w-4 h-4" />
                Generate New Article
              </>
            )}
          </Button>
        </div>

        {/* News List */}
        {newsList.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No articles yet. Generate your first one!</p>
          </div>
        ) : (
          <div className="space-y-8">
            {newsList.map((news) => (
              <article
                key={news.id}
                className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow"
                data-testid={`news-article-${news.id}`}
              >
                <div className="flex items-center space-x-2 text-sm text-gray-500 mb-3">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(news.date)}</span>
                  <span className="text-gray-300">•</span>
                  <span className="inline-block px-2 py-0.5 bg-secondary text-primary text-xs font-medium rounded-full">
                    {news.category}
                  </span>
                </div>
                <h2 className="font-playfair text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {news.title}
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {news.content}
                  </p>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyNews;